import {
  MuiAutocomplete,
  MuiListSubheader,
  MuiTextField,
  MuiTypography,
} from '@/features/shared/material-ui';
import { useMediaQuery } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { AutocompleteRenderGroupParams, createFilterOptions } from '@material-ui/lab/Autocomplete';
import React, {
  Children,
  cloneElement,
  ComponentType,
  createContext,
  forwardRef,
  HTMLAttributes,
  isValidElement,
  memo,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { ListChildComponentProps, VariableSizeList } from 'react-window';

const LISTBOX_PADDING = 8; // px

function renderRow(props: ListChildComponentProps) {
  const { data, index, style } = props;
  return cloneElement(data[index], {
    style: {
      ...style,
      top: (style.top as number) + LISTBOX_PADDING,
    },
  });
}

const OuterElementContext = createContext({});

const OuterElementType = forwardRef<HTMLDivElement>((props, ref) => {
  const outerProps = useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

const filterOptions = createFilterOptions({
  matchFrom: 'start',
  stringify: (option: any) => option.label,
});

function useResetCache(data: any) {
  const ref = useRef<VariableSizeList>(null);
  useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [data]);
  return ref;
}

// Adapter for react-window
const ListboxComponent = forwardRef<HTMLDivElement>(function ListboxComponent(props: any, ref) {
  const { children, ...other } = props;
  const itemData = Children.toArray(children);
  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up('sm'), { noSsr: true });
  const itemCount = itemData.length;
  const itemSize = smUp ? 36 : 48;

  const getChildSize = (child: ReactNode) => {
    if (isValidElement(child) && child.type === MuiListSubheader) {
      return 48;
    }

    return itemSize;
  };

  const getHeight = () => {
    if (itemCount > 8) {
      return 8 * itemSize;
    }
    return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
  };

  const gridRef = useResetCache(itemCount);

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <VariableSizeList
          itemData={itemData}
          height={getHeight() + 2 * LISTBOX_PADDING}
          width="100%"
          ref={gridRef}
          outerElementType={OuterElementType}
          innerElementType="ul"
          itemSize={(index) => getChildSize(itemData[index])}
          overscanCount={5}
          itemCount={itemCount}
        >
          {renderRow}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});

const MarketoAutocomplete = ({
  classes,
  error,
  disabled,
  attributes,
  label,
  value,
  onChange,
  selectIconClassName,
  options,
}: any) => {
  const [inputValue, setInputValue] = useState<string>(value[attributes.name] || '');
  const handleInputChange = useCallback((e: any, val: string) => {
    setInputValue(val);
  }, []);

  useEffect(() => {
    setInputValue(value[attributes.name]);
  }, [attributes.name, value]);

  return (
    <MuiAutocomplete
      onChange={onChange}
      className={classes.select}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      disableListWrap
      ListboxComponent={ListboxComponent as ComponentType<HTMLAttributes<HTMLElement>>}
      filterOptions={filterOptions}
      getOptionSelected={(option: any) => option.value === value[attributes.name]}
      getOptionLabel={(option: any) => option.label || ''}
      options={options}
      disabled={disabled}
      fullWidth
      size="small"
      popupIcon={<ExpandMoreIcon className={selectIconClassName} fontSize="small" />}
      renderInput={(params: any) => (
        <MuiTextField
          {...params}
          label={label}
          variant="outlined"
          error={Boolean(error) || /true/.test(attributes['aria-invalid'])}
          helperText={error || ''}
          inputProps={{ ...params.inputProps, ...attributes }}
        />
      )}
      renderOption={(option: any) => <MuiTypography noWrap>{option?.label}</MuiTypography>}
    />
  );
};

export default memo(MarketoAutocomplete);
